import { Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { BadgeCheck, Clock, Flame, ShieldCheck, ShoppingBag, Sparkles, Star, Store, Trophy } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { useT } from "@/i18n";
import { useAuth } from "@/hooks/useAuth";
import { useReferralCapture } from "@/hooks/useReferral";
import { listingCreatorQuery, creatorPagePath } from "@/lib/marketplace/creator";
import { formatUsd, servicePath } from "@/lib/marketplace/queries";

export function ListingCreatorCard({
  ownerUserId,
  listingId,
}: {
  ownerUserId: string | undefined;
  listingId: string;
}) {
  const { t } = useT();
  const { user } = useAuth();
  const { data } = useQuery(listingCreatorQuery(ownerUserId, listingId));
  // Attribute signups from a listing page to the listing owner even without ?ref=.
  useReferralCapture(data?.profile?.referral_code ?? null);
  if (!data) return null;

  const { profile, stats, rankMeta, services } = data;
  const name = profile.display_name || profile.pro_name || profile.handle || t("profiles.creator");
  const page = creatorPagePath(profile);
  const isSeller = Boolean(stats && stats.seller_status === "seller_approved");
  const linkedService = services.find((s) => s.listing_id === listingId);
  const otherServices = services.filter((s) => s.id !== linkedService?.id).slice(0, 3);
  const sellerRef = { slug: profile.slug, handle: profile.handle };

  return (
    <section className="card-surface overflow-hidden">
      <div className="flex items-start gap-4 p-5">
        <Link {...page} className="shrink-0">
          {profile.avatar_url ? (
            <img
              src={profile.avatar_url}
              alt={name}
              className="size-16 rounded-2xl border border-border object-cover"
              loading="lazy"
            />
          ) : (
            <div className="flex size-16 items-center justify-center rounded-2xl bg-accent font-display text-2xl font-bold text-accent-foreground">
              {name.charAt(0).toUpperCase()}
            </div>
          )}
        </Link>
        <div className="min-w-0 flex-1">
          <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
            {t("profiles.creatorBehind")}
          </p>
          <Link {...page} className="block truncate font-display text-lg font-bold hover:text-primary">
            {name}
          </Link>
          {profile.headline && (
            <p className="line-clamp-2 text-sm text-muted-foreground">{profile.headline}</p>
          )}
          <div className="mt-2 flex flex-wrap items-center gap-1.5">
            {isSeller && (
              <Badge className="gap-1 bg-gradient-to-r from-primary to-primary/70 text-primary-foreground">
                <Store className="size-3" /> {t("profiles.marketplaceSeller")}
              </Badge>
            )}
            {rankMeta && (
              <Badge variant="secondary" className="gap-1">
                <Trophy className="size-3" /> {rankMeta.name}
              </Badge>
            )}
            {profile.is_ambassador && (
              <Badge variant="outline" className="gap-1">
                <Sparkles className="size-3" /> {profile.ambassador_title || t("profiles.endorseAmbassador")}
              </Badge>
            )}
            {stats?.identity_verified && (
              <Badge variant="outline" className="gap-1">
                <BadgeCheck className="size-3" /> {t("profiles.verified")}
              </Badge>
            )}
            {stats?.business_verified && (
              <Badge variant="outline" className="gap-1">
                <ShieldCheck className="size-3" /> {t("profiles.businessVerified")}
              </Badge>
            )}
          </div>
        </div>
      </div>

      {stats && (
        <div className="grid grid-cols-2 gap-px border-y border-border bg-border sm:grid-cols-4">
          <Metric
            label={t("profiles.ordersCompleted")}
            value={String(stats.completed_orders ?? 0)}
            icon={ShoppingBag}
          />
          <Metric
            label={t("profiles.rating")}
            value={stats.rating_count ? `${(stats.rating_avg ?? 0).toFixed(1)} (${stats.rating_count})` : "—"}
            icon={Star}
          />
          <Metric
            label={t("profiles.onTime")}
            value={stats.on_time_rate != null ? `${Math.round(Number(stats.on_time_rate) * 100)}%` : "—"}
            icon={Clock}
          />
          <Metric
            label={t("profiles.buyers")}
            value={String(stats.unique_buyers ?? 0)}
            icon={BadgeCheck}
          />
        </div>
      )}

      <div className="space-y-3 p-5">
        {linkedService && (
          <Link
            {...servicePath(sellerRef, linkedService.slug, profile.id)}
            className="flex items-center gap-3 rounded-xl border border-primary/30 bg-primary/5 p-3 transition-colors hover:bg-primary/10"
          >
            {linkedService.cover_url && (
              <img
                src={linkedService.cover_url}
                alt=""
                className="size-12 shrink-0 rounded-lg object-cover"
                loading="lazy"
              />
            )}
            <div className="min-w-0 flex-1">
              <p className="text-[11px] font-semibold uppercase tracking-widest text-primary">
                {t("profiles.serviceForListing")}
              </p>
              <p className="truncate text-sm font-semibold">{linkedService.title}</p>
              <p className="text-xs text-muted-foreground">
                {t("profiles.from")} {formatUsd(linkedService.starting_price_cents)} ·{" "}
                {t("profiles.daysDelivery", { n: linkedService.delivery_days })}
              </p>
            </div>
          </Link>
        )}

        {otherServices.length > 0 && (
          <>
            {linkedService && <Separator />}
            <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
              {t("profiles.moreServices")}
            </p>
            <ul className="space-y-2">
              {otherServices.map((s) => (
                <li key={s.id}>
                  <Link
                    {...servicePath(sellerRef, s.slug, profile.id)}
                    className="flex items-center gap-3 rounded-lg p-2 transition-colors hover:bg-muted"
                  >
                    {s.cover_url ? (
                      <img src={s.cover_url} alt="" className="size-10 shrink-0 rounded-md object-cover" loading="lazy" />
                    ) : (
                      <div className="size-10 shrink-0 rounded-md bg-muted" />
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium">{s.title}</p>
                      <p className="text-xs text-muted-foreground">
                        {t("profiles.from")} {formatUsd(s.starting_price_cents)}
                      </p>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          </>
        )}

        {!user && (
          <div className="flex flex-col items-center gap-3 rounded-xl border border-primary/30 bg-gradient-to-r from-primary/10 via-transparent to-signal/10 p-4 text-center sm:flex-row sm:text-left">
            <Flame className="size-7 shrink-0 text-primary" />
            <div className="min-w-0 flex-1">
              <p className="font-display font-bold">{t("profiles.joinCtaTitle", { name })}</p>
              <p className="text-xs text-muted-foreground">{t("profiles.joinCtaSubtitle")}</p>
            </div>
            <div className="flex shrink-0 gap-2">
              <Button asChild variant="hot" size="sm">
                <Link to="/login">{t("profiles.joinCtaRegister")}</Link>
              </Button>
              <Button asChild variant="outline" size="sm">
                <Link to="/login">{t("profiles.joinCtaLogin")}</Link>
              </Button>
            </div>
          </div>
        )}

        <div className="flex flex-wrap gap-2 pt-1">
          <Button asChild variant="outline" size="sm">
            <Link {...page}>{t("profiles.viewCreatorPage")}</Link>
          </Button>
          {isSeller && (
            <Button asChild variant="hot" size="sm">
              <Link to="/marketplace/explore" search={{ q: name }}>
                {t("profiles.hireOnMarketplace")}
              </Link>
            </Button>
          )}
        </div>
      </div>
    </section>
  );
}

function Metric({
  label,
  value,
  icon: Icon,
}: {
  label: string;
  value: string;
  icon: typeof Star;
}) {
  return (
    <div className="bg-card p-3 text-center">
      <p className="flex items-center justify-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        <Icon className="size-3" /> {label}
      </p>
      <p className="mt-0.5 font-display text-sm font-bold">{value}</p>
    </div>
  );
}
